// App shell — header with logo + nav + auth status, content area beneath.
// The visual brief: quiet, writerly, off-white parchment, generous spacing.
// Nav collapses to a hamburger drawer below the `md` breakpoint.

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';

const NAV_ITEMS = [
  { to: '/projects', label: 'Projects' },
  { to: '/voice-clones', label: 'Voices' },
  { to: '/batch', label: 'Batch' },
  { to: '/prompts', label: 'Prompts' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/api-keys', label: 'API' },
  { to: '/settings', label: 'Settings' },
];

export function Layout() {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = state.status === 'authed';
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const signOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-full bg-parchment text-ink">
      <header className="border-b border-soft/80 bg-parchment/90 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <svg width="28" height="28" viewBox="0 0 32 32" className="opacity-90 group-hover:opacity-100 transition-opacity">
              <rect width="32" height="32" rx="6" fill="#efeae0" />
              <path d="M8 22V10l8 6 8-6v12" stroke="#2a2a2a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="font-serif text-xl tracking-tight">Scriptvoice</span>
          </Link>

          {isAuthed && (
            <>
              {/* Desktop nav */}
              <nav className="hidden md:flex items-center gap-1 text-sm">
                {NAV_ITEMS.map((item) => (
                  <NavTab key={item.to} to={item.to}>
                    {item.label}
                  </NavTab>
                ))}
                <button
                  onClick={signOut}
                  className="ml-3 px-3 py-1.5 rounded-md text-muted hover:text-ink hover:bg-soft/80 transition-colors"
                >
                  Sign out
                </button>
              </nav>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden p-2 -mr-2 rounded-md text-ink hover:bg-soft/70 transition-colors"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>

        {/* Mobile drawer */}
        {isAuthed && mobileOpen && (
          <nav className="md:hidden border-t border-soft/80 bg-parchment animate-fade-in">
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1 text-sm">
              {NAV_ITEMS.map((item) => (
                <MobileNavTab key={item.to} to={item.to}>
                  {item.label}
                </MobileNavTab>
              ))}
              <button
                onClick={signOut}
                className="text-left px-3 py-2.5 rounded-md text-muted hover:text-ink hover:bg-soft/70 transition-colors"
              >
                Sign out
              </button>
            </div>
          </nav>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Outlet key={location.pathname} />
      </main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-12 mt-12 border-t border-soft/80 text-xs text-muted/70">
        Scriptvoice · directing layer + provider-neutral renders
      </footer>
    </div>
  );
}

function NavTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-md transition-colors ${
          isActive ? 'bg-soft text-ink' : 'text-muted hover:text-ink hover:bg-soft/60'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileNavTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2.5 rounded-md transition-colors ${
          isActive ? 'bg-soft text-ink font-medium' : 'text-muted hover:text-ink hover:bg-soft/60'
        }`
      }
    >
      {children}
    </NavLink>
  );
}
